/**
 * VariantAutomationRedirect — legacy `/workspaces/<wid>/variants/<vid>/automation`
 * URL handler (#1184, Slice 1 of meta #1183).
 *
 * The standalone variant Automation page retired in this slice; its
 * route now redirects the operator to the parent App's Integrations
 * tab with the variant pre-selected in the trigger-rules drawer.
 * The variant→app mapping comes from `useAppContext().allVariants`
 * (loaded at workspace context bootstrap), so the redirect resolves
 * in-memory without a separate fetch.
 *
 * Extracted into its own module so the route-shape test imports the
 * production redirect rather than a hand-replicated copy — Copilot
 * caught the divergence risk on the first PR draft and the extraction
 * is the right structural fix.
 *
 * Edge cases:
 *  - Variant not found (deleted / wrong workspace) → fall back to the
 *    workspace's Applications list, which at least leaves the user
 *    inside the App configuration tree rather than 404'ing them
 *    midway.
 *  - Variant has no parent app (shouldn't happen, but defensive) →
 *    same fallback.
 *
 * Waits for the workspace context to finish loading before deciding
 * — without that, the very first render fires the fallback because
 * `allVariants` is `[]` until the workspace bootstraps.
 */
import { Navigate, useParams } from 'react-router-dom'
import { useAppContext } from '@/hooks/useAppContext'

export function VariantAutomationRedirect() {
  const { variantId } = useParams<{ workspaceId: string; variantId: string }>()
  const { allVariants, workspaceId } = useAppContext()
  if (!workspaceId) return null
  const variant = allVariants.find((v) => v.id === variantId)
  if (!variant) {
    return <Navigate to={`/workspaces/${workspaceId}/applications`} replace />
  }
  const target =
    `/workspaces/${workspaceId}/applications` +
    `?app=${encodeURIComponent(variant.applicationId)}` +
    `&tab=integrations` +
    `&variantConfig=${encodeURIComponent(variant.id)}` +
    `&variantConfigKind=trigger`
  return <Navigate to={target} replace />
}
